import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
} from '@mui/material'
import { INode, INodeTagRequest } from 'shared/types/tree'
import { useNavigateTo, useParams } from 'infrastructure/routing'
import { useSearchParams } from 'shared/hooks'
import { useGetTreeById, useResetTree } from '../../reducers/trees_hooks'
import { nodeForId } from '../utility/TreeHelpers'
import { treeManagementToolUrl } from './TreeActions'
import { useNodeTagOperations } from 'api/tree.hooks'
import { atom, useAtom } from 'infrastructure/state/jotai.wrapper'

const processingAtom = atom(false)

export const NodeTaggingWrapper = (ids: {
  treeId: string
  versionId: string
}) => {
  const goToNode = useGoToNode(ids)
  const { tree: allTree } = useGetTreeById(ids)
  const [tree, setTree] = React.useState<INode>()

  const [searchParams] = useSearchParams()

  const { NodeId } = useParams()
  const nodeId = NodeId || searchParams.get('nodeId')

  useEffect(() => {
    if (!allTree) {
      setTree(undefined)
      return
    } else if (nodeId) {
      setTree(nodeForId(nodeId, allTree))
    } else {
      setTree(allTree)
    }
  }, [allTree, nodeId])

  if (!tree) return <Box>Loading...</Box>
  return (
    <>
      <Button onClick={() => goToNode()}>Go to Base Node</Button>
      {tree.parent_id && (
        <Button onClick={() => goToNode(tree?.parent_id)}>
          Go to Parent Node
        </Button>
      )}
      <TagNode
        nodeWithChildren={tree}
        treeIDWithVersion={ids}
        goToNode={goToNode}
      />
    </>
  )
}

export function TagNode({
  nodeWithChildren,
  goToNode,
  treeIDWithVersion,
}: {
  nodeWithChildren: INode
  goToNode: (nodeId?: string) => void
  treeIDWithVersion: {
    treeId: string
    versionId: string
  }
}) {
  const resetTree = useResetTree()
  const childNodes = nodeWithChildren.nodes
  function onUpdate() {
    const params = {
      treeId: nodeWithChildren.tree_id,
      versionId: nodeWithChildren.version,
    }
    resetTree(params)
  }

  return (
    <Box>
      <Box>Tag the Nodes with the Appropriate Sku Tags</Box>
      <Box>Multiple tags can be added with comma seperation</Box>
      <Box>
        {nodeWithChildren.type}: {nodeWithChildren.title}
      </Box>
      <Box padding={'10px'}>
        {childNodes.map((child) => {
          return (
            <Node
              key={child.id}
              item={child}
              treeIDWithVersion={treeIDWithVersion}
              onUpdate={onUpdate}
              goToNode={goToNode}
            />
          )
        })}
      </Box>
    </Box>
  )
}

function Node({
  item,
  onUpdate,
  goToNode,
  treeIDWithVersion,
}: {
  item: INode
  onUpdate: () => void
  goToNode: (arg0?: string) => void
  treeIDWithVersion: {
    treeId: string
    versionId: string
  }
}) {
  const [processing, setProcessing] = useAtom(processingAtom)
  const { addNodeTags } = useNodeTagOperations()
  const [skuTag, setSkuTag] = useState<string>('')
  const [applyToChildren, setApplyToChildren] = useState(false)
  const [createProductForTag, setCreateProductForTag] = useState(false)
  const [error, setError] = useState('')

  const onSkuTagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError('')
    setSkuTag(e.target.value.trim())
  }
  const onApplyToChildrenChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setApplyToChildren(checked)
  }
  const onCreateProductForTagChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setCreateProductForTag(checked)
  }
  useEffect(() => {
    if (item.body && item.body['sku_tags']) {
      const bodyTags: string[] = item.body['sku_tags']
      setSkuTag(bodyTags.join(','))
    }
  }, [item])

  function getSkuTagArray() {
    if (skuTag.indexOf(',') > 0) {
      return skuTag.split(',')
    }

    return [skuTag]
  }

  async function updateTags() {
    if (skuTag.length == 0) {
      return
    }
    setProcessing(true)
    const req: INodeTagRequest = {
      tree_id: treeIDWithVersion.treeId,
      version: treeIDWithVersion.versionId,
      node_id: item.id,
      sku_tags: getSkuTagArray(),
      apply_to_subtree: applyToChildren,
      create_products_for_tags: createProductForTag,
    }
    const res = await addNodeTags(req)
    setProcessing(false)
    if (res?.errors) {
      setError(res.errors[0].message)
      return
    }
    onUpdate()
  }

  return (
    <Box key={item.id} padding={'10px'} border={'1px solid'}>
      <Box>
        <Button
          disabled={processing || item.nodes.length == 0}
          onClick={() => goToNode(item.id)}
        >
          <i>{item.type}</i>: {item.title}
        </Button>
      </Box>
      <Box>
        <Box m={2}>
          <FormControlLabel
            control={<Checkbox onChange={onApplyToChildrenChange} />}
            label="Apply Tags to Children"
          />
          <FormControlLabel
            control={<Checkbox onChange={onCreateProductForTagChange} />}
            label="Create Product for Tags (If they don't exist)"
          />
        </Box>
      </Box>
      <TextField
        variant="outlined"
        label="Node Sku Tag"
        value={skuTag}
        onChange={onSkuTagChange}
        fullWidth
      />
      <Box>
        <Button disabled={processing} onClick={updateTags}>
          Apply Tag
        </Button>
      </Box>
      <Box m={2}>{error && <p className="error">{error}</p>} </Box>
    </Box>
  )
}

function useGoToNode({
  treeId,
  versionId,
}: {
  treeId: string
  versionId: string
}) {
  const navigate = useNavigateTo()
  return useCallback(
    (nodeId?: string) => {
      const toolUrl = treeManagementToolUrl({
        treeId,
        versionId,
        nodeId,
        tool: 'Node Tagging',
      })
      navigate(toolUrl)
    },
    [navigate, treeId, versionId],
  )
}
